import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import { getLoginDetails } from "../../utils/storage";
import UploadIcon from "../../icons/upload.svg";
import "./VirtualGrading.css";

// Mock data for grading interface
const mockItem = {
  id: "item_1",
  title: "Catalytic Converter",
  make: "Toyota",
  model: "Camry", 
  year: "2018",
  partNumber: "TC-CC-2018-001",
  description: "Used catalytic converter with some wear",
  images: [
    "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=600&h=400&fit=crop&crop=center",
  ],
  submittedDate: "2024-10-28T19:16:00Z",
  status: "pending",
  userId: "user_123",
  userName: "John Doe",
  chatMessages: [],
};

// Mock price suggestions data
const mockPriceSuggestions = [
  { id: 1, price: 150, description: "Toyota Camry 2018 Catalytic Converter - Good Condition", category: "Catalytic Converter" },
  { id: 2, price: 175, description: "Toyota Camry 2018-2020 Catalytic Converter - Excellent", category: "Catalytic Converter" },
  { id: 3, price: 125, description: "Toyota Camry 2018 Catalytic Converter - Fair Condition", category: "Catalytic Converter" },
  { id: 4, price: 200, description: "Toyota Camry 2018 Catalytic Converter - Like New", category: "Catalytic Converter" },
  { id: 5, price: 100, description: "Toyota Camry 2018 Catalytic Converter - Used", category: "Catalytic Converter" },
  { id: 6, price: 300, description: "Toyota Camry 2018 Catalytic Converter - OEM", category: "Catalytic Converter" },
  { id: 7, price: 80, description: "Toyota Camry 2018 Catalytic Converter - Damaged", category: "Catalytic Converter" },
  { id: 8, price: 250, description: "Toyota Camry 2018 Catalytic Converter - Premium", category: "Catalytic Converter" },
];

const GradeItem = () => {
  const { itemId } = useParams();
  const history = useHistory();
  const { hierarchy } = getLoginDetails();
  
  const [item, setItem] = useState(mockItem);
  const [gradingData, setGradingData] = useState({
    assessedValue: "",
    gradingNotes: "",
    grade: "A", // A, B, C
  });
  const [chatMessage, setChatMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isGrading, setIsGrading] = useState(false);
  const [showGradingForm, setShowGradingForm] = useState(true);
  const [priceInput, setPriceInput] = useState("");
  const [priceSuggestions, setPriceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (hierarchy !== 1) {
      history.push("/virtual-grading");
    }
  }, [hierarchy, history]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.vg-price-input-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleGradingChange = (field, value) => {
    setGradingData({
      ...gradingData,
      [field]: value,
    });
  };

  // Price search functionality
  const handlePriceInputChange = (value) => {
    setPriceInput(value);
    setGradingData({
      ...gradingData,
      assessedValue: value,
    });
    
    if (value.length > 0) {
      searchPriceSuggestions(value);
    } else {
      setPriceSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const searchPriceSuggestions = async (query) => {
    setIsLoadingSuggestions(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const filtered = mockPriceSuggestions.filter(suggestion =>
        suggestion.description.toLowerCase().includes(query.toLowerCase()) ||
        suggestion.price.toString().includes(query)
      );
      setPriceSuggestions(filtered);
      setShowSuggestions(true);
      setIsLoadingSuggestions(false);
    }, 300);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setPriceInput(suggestion.price.toString());
    setGradingData({
      ...gradingData,
      assessedValue: suggestion.price.toString(),
    });
    setShowSuggestions(false);
  };

  const handleDirectPriceSubmit = () => {
    if (priceInput && !isNaN(priceInput)) {
      setGradingData({
        ...gradingData,
        assessedValue: priceInput,
      });
      setSelectedSuggestion(null);
    }
  };

  const handleStartGrading = () => {
    setShowGradingForm(true);
  };

  const handleSubmitGrade = async () => {
    setIsGrading(true);
    
    // TODO: Submit grading data to API
    // const submitGrade = async () => {
    //   await postData(SUBMIT_GRADE_URL, {
    //     itemId: item.id,
    //     grade: gradingData.grade,
    //     assessedValue: gradingData.assessedValue,
    //     notes: gradingData.gradingNotes,
    //   });
    // };
    
    // Simulate API call
    setTimeout(() => {
      setIsGrading(false);
      history.push("/virtual-grading/admin");
    }, 2000);
  };

  const handleReject = async () => {
    setIsGrading(true);
    
    // TODO: Submit rejection to API
    // const rejectItem = async () => {
    //   await postData(REJECT_ITEM_URL, {
    //     itemId: item.id,
    //     reason: gradingData.gradingNotes,
    //   });
    // };
    
    // Simulate API call
    setTimeout(() => {
      setIsGrading(false);
      history.push("/virtual-grading/admin");
    }, 2000);
  };

  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        text: chatMessage,
        timestamp: new Date().toISOString(),
        sender: "admin",
      };
      setChatMessages([...chatMessages, newMessage]);
      setChatMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (hierarchy !== 1) {
    return null; // Will redirect
  }

  return (
    <PageLayout 
      heading="Virtual Grading"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        <div className="vg-grading-layout">
          <div className="vg-header-top">
            <Button
              className="vg-back-button"
              onClick={() => history.goBack()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="mr-8"
              >
                <path
                  fillRule="evenodd"
                  d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                />
              </svg>
              Back
            </Button>
          </div>
          {/* Left Column - Item Details and Grading */}
          <div className="vg-grading-left">
            {/* Item Image */}
            <div className="vg-item-image-section">
              <div className="vg-main-image">
                <img src={item.images[0]} alt={item.title} />
                {/* Annotations would go here - arrows pointing to specific parts */}
                <div className="vg-image-annotations">
                  <div className="vg-annotation-arrow" style={{ top: "30%", left: "20%" }}>
                    <div className="vg-arrow-line"></div>
                    <div className="vg-arrow-head"></div>
                  </div>
                  <div className="vg-annotation-arrow" style={{ top: "60%", right: "25%" }}>
                    <div className="vg-arrow-line"></div>
                    <div className="vg-arrow-head"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Item Information */}
            <div className="vg-info-card">
              <h3 className="vg-card-title">Item Information</h3>
              <div className="vg-info-content">
                <div className="vg-info-item">
                  <span className="vg-info-label">Make:</span>
                  <span className="vg-info-value">{item.make}</span>
                </div>
                <div className="vg-info-item">
                  <span className="vg-info-label">Model:</span>
                  <span className="vg-info-value">{item.model}</span>
                </div>
                <div className="vg-info-item">
                  <span className="vg-info-label">Year:</span>
                  <span className="vg-info-value">{item.year}</span>
                </div>
                <div className="vg-info-item">
                  <span className="vg-info-label">Part Number:</span>
                  <span className="vg-info-value">{item.partNumber}</span>
                </div>
                <div className="vg-info-item">
                  <span className="vg-info-label">Submitted:</span>
                  <span className="vg-info-value">{formatDate(item.submittedDate)}</span>
                </div>
              </div>
            </div>

            {/* Grading Section */}
            <div className="vg-grade-card">
              <h3 className="vg-card-title">Grade This Item</h3>
              {!showGradingForm ? (
                <Button
                  className="vg-start-grading-btn"
                  onClick={handleStartGrading}
                >
                  Start Grading Process
                </Button>
              ) : (
                <div className="vg-grading-form">
                  <div className="vg-form-group">
                    <label className="vg-form-label">Assessed Value ($)</label>
                    <div className="vg-price-input-container">
                      <input
                        type="text"
                        className="vg-form-input vg-price-input"
                        value={priceInput}
                        onChange={(e) => handlePriceInputChange(e.target.value)}
                        placeholder="Enter price or search for suggestions..."
                        autoComplete="off"
                      />
                      {isLoadingSuggestions && (
                        <div className="vg-loading-spinner">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                            className="vg-spinner"
                          >
                            <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM7.5 4a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5z" />
                          </svg>
                        </div>
                      )}
                      {showSuggestions && priceSuggestions.length > 0 && (
                        <div className="vg-suggestions-dropdown">
                          {priceSuggestions.map((suggestion) => (
                            <div
                              key={suggestion.id}
                              className="vg-suggestion-item"
                              onClick={() => handleSuggestionSelect(suggestion)}
                            >
                              <div className="vg-suggestion-price">${suggestion.price}</div>
                              <div className="vg-suggestion-description">{suggestion.description}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedSuggestion && (
                      <div className="vg-selected-suggestion">
                        <span className="vg-selected-label">Selected:</span>
                        <span className="vg-selected-price">${selectedSuggestion.price}</span>
                        <span className="vg-selected-desc">{selectedSuggestion.description}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="vg-form-group">
                    <label className="vg-form-label">Grade</label>
                    <select
                      className="vg-form-select"
                      value={gradingData.grade}
                      onChange={(e) => handleGradingChange("grade", e.target.value)}
                    >
                      <option value="A">A - Excellent</option>
                      <option value="B">B - Good</option>
                      <option value="C">C - Fair</option>
                    </select>
                  </div>
                  
                  <div className="vg-form-group">
                    <label className="vg-form-label">Grading Notes</label>
                    <textarea
                      className="vg-form-textarea"
                      rows="4"
                      value={gradingData.gradingNotes}
                      onChange={(e) => handleGradingChange("gradingNotes", e.target.value)}
                      placeholder="Add detailed notes about the item's condition, any issues, or grading rationale..."
                    />
                  </div>
                  
                  <div className="vg-grading-actions">
                    <Button
                      className="vg-submit-grade-btn"
                      onClick={handleSubmitGrade}
                      disabled={isGrading}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                        className="mr-8"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                        <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                      </svg>
                      Submit Grade
                    </Button>
                    <Button
                      className="vg-reject-btn"
                      onClick={handleReject}
                      disabled={isGrading}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                        className="mr-8"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                      Reject
                    </Button>
                    <Button
                      className="vg-cancel-btn"
                      onClick={() => setShowGradingForm(false)}
                      disabled={isGrading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Chat */}
          <div className="vg-grading-right">
            <div className="vg-chat-card">
              <h3 className="vg-card-title">Chat</h3>
              <div className="vg-chat-content">
                {chatMessages.length === 0 ? (
                  <div className="vg-chat-empty">
                    <div className="vg-chat-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6-.097 1.016-.417 2.13-.771 2.966-.079.186-.074.402.027.533.098.127.24.2.403.2.401 0 .744-.115 1.103-.33 1.927-.88 3.26-2.41 3.843-3.404C7.884 13.43 7.961 13.5 8 13.5c.039 0 .116-.07.189-.167.583-.994 1.916-2.524 3.843-3.404.359.215.702.33 1.103.33.163 0 .305-.073.403-.2.101-.131.106-.347.027-.533-.354-.836-.674-1.95-.771-2.966C15.257 11.37 16 9.76 16 8c0-3.866-3.582-7-8-7z" />
                      </svg>
                    </div>
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="vg-chat-messages">
                    {chatMessages.map((message) => (
                      <div key={message.id} className="vg-chat-message">
                        <div className="vg-message-content">
                          {message.text}
                        </div>
                        <div className="vg-message-time">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="vg-chat-input">
                <input
                  type="text"
                  className="vg-message-input"
                  placeholder="Type a message..."
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  className="vg-send-button"
                  onClick={handleSendMessage}
                  disabled={!chatMessage.trim()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default GradeItem;
